import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'motion/react';
import { Link } from 'react-router-dom';
import {
  Snowflake,
  ArrowRight,
  TrendingUp,
  Users,
  Target,
  Brain,
  Zap,
  Shield,
  BarChart3,
  Gauge,
  RefreshCw,
  Play,
  ChevronRight, 
  Thermometer,
  Radio,
  Settings,
  Waves,
  LineChart,
  Building2,
  DollarSign,
  Server,
  Wrench,
  TrendingDown
} from 'lucide-react';
import { ImageWithFallback } from '../../figma/ImageWithFallback';
import { ProductTestimonials } from '../../shared/ProductTestimonials';
import { aiCoolerTestimonials } from '../../shared/testimonialData';

const AICooler: React.FC = () => {
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: containerRef });
  const backgroundY = useTransform(scrollYProgress, [0, 1], ['0%', '50%']);

  const features = [
    {
      icon: Brain,
      title: 'AI-Driven Optimization',
      description: 'AI Cooler is more than a “smart thermostat.” It runs an end-to-end optimization sequence starting from installation and onboarding, through grid-signal sensing, occupancy AI, and load forecasting, right through to remote diagnostics and performance reporting.'
    },
    {
      icon: Gauge,
      title: 'Demand Response Ready',
      description: 'Environmental sensors feed live data on temperature, humidity, and usage patterns into cloud analytics. Machine learning models then determine optimal cooling strategies for each site, balancing comfort, efficiency, and grid conditions. The system automatically adjusts setpoints and compressor behavior, while a memory layer personalizes responses to recurring patterns such as peak occupancy hours or specific site constraints.'
    },
    {
      icon: Wrench,
      title: 'Predictive Maintenance',
      description: 'The platform is delivered as a scalable SaaS solution so OEMs, ESCOs, and utilities can roll it out quickly across regions and customer segments. Channel partners can bundle financing, performance guarantees, and rebate management on top, turning smart AC into a strong commercial offering rather than just another piece of equipment.'
    },

  ];

  const optimizationFlow = [
    {
      icon: Radio,
      title: 'Data Collection',
      subtitle: 'Environmental sensors gather data',
      description: 'Environmental sensors gather and transmit temperature and humidity data from multiple zones for comprehensive monitoring.',
      color: 'from-cyan-400 to-blue-500',
      step: 1
    },
    {
      icon: Brain,
      title: 'AI Analysis',
      subtitle: 'Machine learning processes conditions',
      description: 'Machine learning algorithms process environmental conditions, occupancy patterns, and grid signals to understand system behavior.',
      color: 'from-blue-400 to-indigo-500',
      step: 2
    },
    {
      icon: Settings,
      title: 'Optimization',
      subtitle: 'AI determines optimal settings',
      description: 'AI determines optimal cooling settings for energy efficiency, balancing comfort requirements with cost reduction goals.',
      color: 'from-indigo-400 to-purple-500',
      step: 3
    },
    {
      icon: Snowflake,
      title: 'Cooling Control',
      subtitle: 'System adjusts cooling output',
      description: 'System adjusts cooling output based on AI recommendations, modulating compressor speed and airflow in real time.',
      color: 'from-purple-400 to-pink-500',
      step: 4
    },
    {
      icon: LineChart,
      title: 'Performance Monitoring',
      subtitle: 'Continuous tracking of efficiency',
      description: 'Continuous tracking of energy savings and system efficiency with alerts for anomalies and optimization opportunities.',
      color: 'from-pink-400 to-rose-500',
      step: 5
    }
  ];

  const solutions = [
    {
      icon: Gauge,
      title: 'Hyperscale Data Centers',
      description: 'AI Cooler stabilizes thermal conditions in data halls by continuously tuning cooling based on real-time IT load, external weather, and grid price signals. This reduces over-cooling, improves PUE, and helps operators hit aggressive sustainability targets without risking uptime.',
    },
    {
      icon: Waves,
      title: 'Commercial & Industrial Property Portfolios',
      description: 'Facility managers for malls, office parks, factories, and warehouses use the platform to manage thousands of split units from a single console. Occupancy-aware optimization reduces wasted cooling in underused spaces while keeping front-of-house areas comfortable for staff and visitors.',
    },
    {
      icon: Shield,
      title: 'Utility & ESCO Demand-Response Programs',
      description: 'Utilities and ESCOs integrate AI Cooler into demand-response schemes so that enrolled sites can automatically adjust cooling during peak events. The platform aggregates small setpoint changes across many units into meaningful grid relief, while still protecting occupant comfort and capturing rebates.',
    },
    {
      icon: DollarSign,
      title: 'OEM Smart AC Offerings',
      description: 'Original equipment manufacturers embed AI Cooler intelligence into their product lines, transforming standard split ACs into connected, value-added solutions. This allows OEMs to offer performance-based contracts, remote services, and differentiated warranties driven by real operating data.',
    },
    {
      icon: BarChart3,
      title: 'Channel Partner & Installer Ecosystems',
      description: 'Installation and service partners bundle AI Cooler with financing and long-term maintenance packages. Remote diagnostics and predictive maintenance workflows cut truck rolls, strengthen customer relationships, and create recurring service revenue beyond the initial equipment sale.',
    }
  ];

  const compliances = [
    {
      icon: Building2,
      title: 'Secure Device Enrollment and Identity',
      description: 'Every AI Cooler unit and gateway is provisioned with unique credentials, ensuring that only authenticated devices can connect to the cloud platform or receive remote commands. This protects both building systems and grid-facing programs from unauthorized access.',
      benefits: ['Product Differentiation', 'Revenue Per Unit', 'Service Contracts', 'Brand Value', 'Market Leadership']
    },
    {
      icon: Target,
      title: 'Encrypted Telemetry and Control Channels',
      description: 'Environmental data, control signals, and optimization outputs are transmitted over encrypted links end-to-end. This safeguards sensitive information about occupancy patterns, facility layouts, and energy profiles while meeting enterprise IT expectations.',
      benefits: ['Lower OPEX', 'Tenant Comfort', 'Asset Value', 'Sustainability Goals', 'Portfolio Analytics']
    },
    {
      icon: Server,
      title: 'Role-Based Access and Portfolio Segmentation',
      description: 'Access to dashboards and APIs is governed by clearly defined roles. Global portfolio managers, regional engineers, site technicians, OEM teams, and utility partners each see only the assets and insights relevant to their responsibilities, with strict segmentation between customers and projects.',
      benefits: ['PUE Reduction', 'Uptime Assurance', 'Cost Savings', 'Carbon Reduction', 'Capacity Planning']
    },
    {
      icon: Users,
      title: 'Audit-Ready Logs and Compliance Reporting',
      description: 'All major actions setpoint changes, demand-response events, firmware updates, and configuration edits are logged with timestamps and attribution. This creates a clean audit trail for rebates, performance contracts, and regulatory checks around efficiency and grid-support programs.',
      benefits: ['Installation Revenue', 'Service Contracts', 'Customer Retention', 'Competitive Edge', 'Training Support']
    },
    {
      icon: Shield,
      title: 'Alignment with Energy & Sustainability Standards',
      description: 'The platform is designed to support reporting frameworks linked to building codes, ESG disclosure, and efficiency mandates across different regions. Aggregated data can feed into compliance submissions, carbon accounting, and sustainability dashboards at corporate level.',
      benefits: ['Regulatory Adherence', 'Risk Mitigation', 'Customer Trust', 'Market Access', 'Long-Term Viability']
    }
  ];

  const benefits = [
    {
      icon: Zap,
      title: 'Lower Energy Bills and Stronger Margins',
      description: 'AI-driven optimization cuts unnecessary cooling while maintaining comfort, delivering measurable kWh savings across large fleets of AC units. Over time, this reduces operating expenditure and helps organizations protect margins in energy-intensive operations.',
    },
    {
      icon: TrendingUp,
      title: 'Demand-Response Revenue and Rebates',
      description: 'By being grid-aware and demand-response ready, AI Cooler allows facilities to participate in utility programs that pay for flexible load. This turns AC systems into active financial assets, unlocking new revenue streams and shorter payback periods on upgrades.',
    },
    {
      icon: TrendingDown,
      title: 'Fewer Breakdowns and Longer Equipment Life',
      description: 'Predictive maintenance and remote diagnostics detect anomalies before they become failures. Compressors and critical components are operated within healthier ranges, which reduces emergency call-outs, extends asset life, and stabilizes maintenance budgets.',
    },
    {
      icon: DollarSign,
      title: 'Portfolio-Level Visibility and Control',
      description: 'Operations leaders gain a single, consolidated view of comfort, consumption, and performance KPIs across sites, regions, and customer segments. This supports smarter capital planning, targeted retrofits, and data-driven conversations with finance and ESG teams.',
    },
    {
      icon: Thermometer,
      title: 'Better Occupant Experience and Brand Value',
      description: 'Personalized control patterns and smoother temperature management make spaces consistently comfortable, whether they are offices, retail floors, or data halls. The combination of comfort and visibly lower energy use strengthens brand perception with tenants, employees, and end customers.',
    }
  ];

  return (
    <div ref={containerRef} className="relative min-h-screen pt-20">
      {/* Animated Background */}
      <motion.div
        style={{ y: backgroundY }}
        className="fixed inset-0 opacity-20"
      >
        <div className="absolute inset-0">
          {Array.from({ length: 8 }).map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-6 h-6 bg-cyan-500 rounded-full"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
              }}
              animate={{
                scale: [0, 1, 0],
                opacity: [0, 1, 0],
              }}
              transition={{
                duration: 7,
                delay: i * 0.6,
                repeat: Infinity,
                repeatType: "loop"
              }}
            />
          ))}
        </div>
      </motion.div>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Content */}
            <motion.div
              initial={{ opacity: 0, x: -100 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="flex items-center gap-3 mb-6"
              >
                <div className="p-3 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 neon-glow">
                  <Snowflake className="w-8 h-8 text-white" />
                </div>
                <div>
                  <span className="text-sm font-medium text-cyan-400 uppercase tracking-wide">AI-Enhanced Split AC</span>
                  <h1 className="text-4xl lg:text-6xl font-bold">
                    <span className="bg-gradient-to-r from-cyan-500 to-blue-600 bg-clip-text text-transparent">AI Cooler</span>
                  </h1>
                  <p className="text-sm text-muted-foreground italic">Smart AC Platform</p>
                </div>
              </motion.div>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="text-xl text-muted-foreground mb-8 leading-relaxed text-justify"
              >
AI Cooler is an AI-enhanced smart split AC platform built for serious energy users—OEMs, facility managers, ESCOs, utilities, and hyperscale data centers. Instead of treating air-conditioning as a fixed cost, it turns every unit into an intelligent asset that listens to the grid, learns from occupancy, and continuously tunes performance. The platform couples advanced environmental sensing with cloud analytics to deliver demand-response readiness, energy rebates, and predictable comfort. It fits neatly into existing building and data-center infrastructure, helping organizations stay ahead of efficiency mandates while avoiding disruptive retrofits.
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.6 }}
                className="flex flex-col sm:flex-row gap-4 mb-12"
              >
                <Link
                  to="/contact"
                  className="group inline-flex items-center justify-center gap-3 bg-gradient-to-r from-cyan-500 to-blue-600 px-8 py-4 rounded-xl text-white font-semibold hover:shadow-2xl hover:shadow-cyan-500/25 transition-all duration-300"
                >
                  Request Platform Demo
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Link>
                <button className="inline-flex items-center justify-center gap-3 glass px-8 py-4 rounded-xl text-white font-semibold hover:glass-strong transition-all duration-300">
                  <Play className="w-5 h-5" />
                  Watch Demo
                </button>
              </motion.div>

              {/* Key Metrics */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.8 }}
                className="grid grid-cols-3 gap-8 hidden"
              >
                <div className="text-center">
                  <div className="text-2xl font-bold text-cyan-500">AI</div>
                  <div className="text-sm text-muted-foreground">Enhanced</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-400">Smart</div>
                  <div className="text-sm text-muted-foreground">Cooling</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-accent">Energy</div>
                  <div className="text-sm text-muted-foreground">Rebates</div>
                </div>
              </motion.div>
            </motion.div>

            {/* Hero Image */}
            <motion.div
              initial={{ opacity: 0, x: 100, rotateY: -15 }}
              animate={{ opacity: 1, x: 0, rotateY: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="relative"
            >
              <div className="relative group">
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 25, repeat: Infinity, ease: 'linear' }}
                  className="absolute -inset-4 bg-gradient-to-r from-cyan-500 via-blue-500 to-indigo-500 rounded-2xl blur-3xl opacity-20"
                />
                <div className="relative overflow-hidden rounded-2xl border border-white/10 shadow-2xl">
                  <ImageWithFallback
                    src="/assets/Products/iot-Products/AICooler.png"
                    alt="AI Cooler Smart AC Platform"
                    className="w-full h-auto"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-transparent" />
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Core Features Section */}
      <section className="relative py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-5xl mb-4">
              Core <span className="bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">Features</span>
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto text-center">
              Intelligent cooling solutions powered by advanced AI
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="glass-strong rounded-xl p-6 hover:scale-105 transition-all duration-300 group"
                >
                  <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-cyan-500/20 to-blue-500/20 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                    <Icon className="w-6 h-6 text-cyan-400" />
                  </div>
                  <h3 className="font-semibold text-lg mb-2 text-white hidden">{feature.title}</h3>
                  <p className="text-sm text-muted-foreground text-justify">{feature.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* AI Cooler Optimization Flow - Hardcoded from Image */}
      <section className="relative py-24 bg-white/[0.02] hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-5xl mb-4">
              AI Cooler <span className="bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">Optimization Flow</span>
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-4 text-center">
              Optimization Sequence
            </p>
            <p className="text-base text-muted-foreground max-w-4xl mx-auto text-center">
              The optimization flow includes install, grid signal sensing, occupancy AI, load forecasting, efficiency tuning, rebate reporting, and remote diagnostics. Channel partners bundle financing.
            </p>
          </motion.div>

          {/* Hardcoded Flowchart from Provided Image */}
          <div className="relative">
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4 md:gap-6 relative">
              {optimizationFlow.map((step, index) => {
                const Icon = step.icon;
                return (
                  <React.Fragment key={index}>
                    <motion.div
                      initial={{ opacity: 0, y: 30 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.5, delay: index * 0.1 }}
                      className="glass-strong rounded-xl p-6 relative group hover:border-cyan-500/50 transition-all duration-300"
                    >
                      <div className="flex flex-col items-center text-center">
                        <div className={`w-16 h-16 rounded-full bg-gradient-to-br ${step.color} flex items-center justify-center mb-4 shadow-lg shadow-cyan-500/30 group-hover:scale-110 transition-transform duration-300`}>
                          <Icon className="w-8 h-8 text-white" />
                        </div>
                        <h3 className="font-semibold text-lg mb-2 text-white">{step.title}</h3>
                        <p className="text-xs text-muted-foreground mb-3">{step.subtitle}</p>
                        <p className="text-xs text-muted-foreground/80 text-justify">{step.description}</p>
                      </div>
                      {/* Arrow */}
                      {index < optimizationFlow.length - 1 && (
                        <div className="hidden md:block absolute top-1/2 -right-3 transform -translate-y-1/2 z-10">
                          <ChevronRight className="w-6 h-6 text-cyan-400" />
                        </div>
                      )}
                    </motion.div>
                  </React.Fragment>
                );
              })}
            </div>

            {/* Continuous Improvement Note */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="mt-12 glass rounded-xl p-6 border border-cyan-500/30"
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-cyan-500/20 to-blue-500/20 flex items-center justify-center flex-shrink-0">
                  <RefreshCw className="w-6 h-6 text-cyan-400" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg mb-2 text-white">Continuous Improvement</h3>
                  <p className="text-sm text-muted-foreground text-justify">
                    AI Cooler continuously improves its optimization capabilities, ensuring that users have access to the latest insights and tools for their business. The system learns from historical performance data, weather patterns, and occupancy trends to refine cooling strategies and maximize energy savings over time.
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Platform Capabilities Section */}
      <section className="relative py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-5xl mb-4">
               <span className="bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">Solutions</span>
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto text-center">
            
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {solutions.map((solution, index) => {
              const Icon = solution.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="glass-strong rounded-xl p-8 hover:scale-105 transition-all duration-300"
                >
                  <div className="flex items-start gap-4 mb-6">
                    <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-cyan-500/20 to-blue-500/20 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-7 h-7 text-cyan-400" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-xl mb-2 text-white">{solution.title}</h3>
                      <p className="text-sm text-muted-foreground text-justify">{solution.description}</p>
                    </div>
                  </div>

                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Target Markets Section */}
      <section className="relative py-24 bg-white/[0.02]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-5xl mb-4">
              Data Security & <span className="bg-gradient-to-r from-blue-400 to-indigo-400 bg-clip-text text-transparent"> Compliance </span>
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto text-center">
              
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {compliances.map((compliance, index) => {
              const Icon = compliance.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="glass-strong rounded-xl p-8 hover:scale-105 transition-all duration-300"
                >
                  <div className="flex items-start gap-4 mb-6">
                    <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-blue-500/20 to-indigo-500/20 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-7 h-7 text-blue-400" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-xl mb-2 text-white">{compliance.title}</h3>
                      <p className="text-sm text-muted-foreground text-justify">{compliance.description}</p>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="relative py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-5xl mb-4">
              Platform <span className="bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">Benefits</span>
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto text-center">
              Transform HVAC operations with intelligent automation
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="glass rounded-xl p-6 border-l-4 border-cyan-500/50 hover:border-cyan-500 transition-all duration-300"
                >
                  <Icon className="w-8 h-8 text-cyan-400 mb-3" />
                  <h3 className="font-semibold text-lg mb-2 text-white">{benefit.title}</h3>
                  <p className="text-sm text-muted-foreground mb-3 text-justify">{benefit.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="relative py-24 bg-white/[0.02] hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-5xl mb-4">
              Customer <span className="bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">Testimonials</span>
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto text-center">
              What our customers are saying about AI Cooler
            </p>
          </motion.div>

          <ProductTestimonials 
            testimonials={aiCoolerTestimonials}
            title="Data Center Cooling Excellence"
            subtitle="See how organizations achieved massive energy savings with AI Cooler"
            gradientFrom="#00CFFF"
            gradientVia="#3B82F6"
            gradientTo="#00CFFF"
            iconColor="#00CFFF"
            dotColor="bg-cyan-500"
            speed={65}
            direction="left"
          />
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="glass-strong rounded-2xl p-12 text-center border border-cyan-500/30 relative overflow-hidden"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/10 via-transparent to-blue-500/10" />
            <div className="relative z-10">
              <h2 className="text-3xl lg:text-4xl mb-4 text-white">
                Ready to Transform Your <span className="bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">Cooling Infrastructure?</span>
              </h2>
              <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto text-justify">
                Schedule a demo to see how AI Cooler can reduce energy costs, improve efficiency, and unlock demand-response revenue for your facility.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center gap-3 bg-gradient-to-r from-cyan-500 to-blue-600 text-white px-8 py-4 rounded-xl font-semibold shadow-lg shadow-cyan-500/25 hover:shadow-cyan-500/40 transition-all duration-300"
                >
                  Schedule Demo
                  <ArrowRight className="w-5 h-5" />
                </Link>
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center gap-3 glass px-8 py-4 rounded-xl font-semibold border border-cyan-500/30 hover:border-cyan-500/50 transition-all duration-300 text-white"
                >
                  Contact Sales
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default AICooler;